import { useEffect, useState } from 'react';
import { FieldValues, Path, UseControllerReturn } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { Url } from '@rossum/api-client';
import { SchemaSection } from '@rossum/api-client/schemas';
import { ContentWrapper } from '@rossum/components/content-group/ContentWrapper';
import { SplitContent } from '@rossum/components/content-group/SplitContent';
import {
  IconCode,
  IconCodeOff,
  IconCustomAurora,
} from '@rossum/ui/icons/tabler';
import {
  Button,
  Collapse,
  Divider,
  IconButton,
  Stack,
  SvgIcon,
  Tooltip,
  Typography,
} from '@rossum/ui/material';
import { docsLinks } from '../../../constants/values';
import { link } from '../../../lib/formaterValues';
import { FormulaEditor } from '../../formulas/FormulaEditor/FormulaEditor';
import { getValidSchemaFields } from '../../formulas/FormulaEditor/helpers';
import { SummaryBox } from '../../formulas/FormulaFieldSection/components/SummaryBox';
import { TriggerCopilot } from '../copilots/TriggerCopilot';
import { useSuggestTriggerConditionSummary } from '../hooks/useSuggestTriggerConditionSummary';
import { useValidateTrigger } from '../hooks/useValidateTrigger';
import { ValidRuleForm } from '../utils';

const MIN_VISIBLE_EDITOR_LINES = 3;

type TriggerProps<T extends FieldValues & ValidRuleForm> = {
  queue: Url;
  schemaContent: SchemaSection[];
  schemaUrl: Url;
  triggerConditionField: UseControllerReturn<T, Path<T>>;
  currentRuleConcept: T;
  validationError: string | undefined;
};

export const TriggerSection = <T extends FieldValues & ValidRuleForm>({
  queue,
  schemaContent,
  schemaUrl,
  triggerConditionField,
  currentRuleConcept,
  validationError,
}: TriggerProps<T>) => {
  const intl = useIntl();

  const [triggerCopilotOpen, setTriggerCopilotOpen] = useState(false);
  const [codeEditorVisible, setCodeEditorVisible] = useState(false);

  const [triggerConditionForSummary, setTriggerConditionForSummary] = useState(
    currentRuleConcept.triggerCondition
  );

  const triggerConditionUpToDate =
    currentRuleConcept.triggerCondition === triggerConditionForSummary;

  const nameMissing = !currentRuleConcept.name.length;

  const {
    data: triggerConditionError,
    isLoading: isValidatingTriggerCondition,
  } = useValidateTrigger(triggerConditionField.field.value);

  const { data: summary, isFetching: isSuggestingSummary } =
    useSuggestTriggerConditionSummary({
      schemaContent,
      queue,
      schemaRule: {
        triggerCondition: triggerConditionForSummary,
      },
    });

  useEffect(() => {
    if (triggerConditionError || validationError) {
      setCodeEditorVisible(true);
    }
  }, [triggerConditionError, validationError]);

  return (
    <>
      <ContentWrapper>
        <SplitContent
          title={intl.formatMessage({
            id: 'features.queueSettings.rules.detail.when.title',
          })}
          description={intl.formatMessage(
            {
              id: 'features.queueSettings.rules.detail.when.description',
            },
            {
              link: link(docsLinks.txscript),
            }
          )}
        >
          {isSuggestingSummary ? (
            <SummaryBox variant="suggestingSummary" />
          ) : nameMissing ? (
            <SummaryBox variant="missingInfoRule" />
          ) : !triggerConditionUpToDate ? (
            <SummaryBox
              variant="editingCode"
              callback={() => {
                if (
                  !triggerConditionError &&
                  !isValidatingTriggerCondition &&
                  !validationError
                ) {
                  setTriggerConditionForSummary(
                    currentRuleConcept.triggerCondition
                  );
                }
              }}
            />
          ) : summary ? (
            <SummaryBox
              value={summary}
              disabled={triggerConditionField.field.disabled}
            />
          ) : (
            <SummaryBox
              variant="emptyRule"
              callback={() => setTriggerCopilotOpen(true)}
              disabled={triggerConditionField.field.disabled}
            />
          )}
        </SplitContent>

        <Stack>
          <Collapse in={codeEditorVisible}>
            <Stack spacing={2} pt={3}>
              <Divider />
              <FormulaEditor
                fields={getValidSchemaFields(schemaContent)}
                value={triggerConditionField.field.value}
                onChange={newValue => {
                  return triggerConditionField.field.onChange(newValue);
                }}
                disabled={triggerConditionField.field.disabled}
                config={{
                  minVisibleLines: MIN_VISIBLE_EDITOR_LINES,
                  showGutters: true,
                }}
                onBlur={triggerConditionField.field.onBlur}
              />
              {triggerConditionError || validationError ? (
                <Typography color="error" variant="body2">
                  {triggerConditionError || validationError}
                </Typography>
              ) : null}
            </Stack>
          </Collapse>
        </Stack>

        <Stack direction="row" spacing={2} justifyContent="end" pt={2}>
          <Tooltip
            title={
              codeEditorVisible
                ? intl.formatMessage({ id: 'features.formulas.hideSourceCode' })
                : intl.formatMessage({ id: 'features.formulas.showSourceCode' })
            }
          >
            <IconButton
              color="inherit"
              onClick={() => setCodeEditorVisible(!codeEditorVisible)}
            >
              <SvgIcon fontSize="large">
                {codeEditorVisible ? <IconCodeOff /> : <IconCode />}
              </SvgIcon>
            </IconButton>
          </Tooltip>

          <Button
            variant="contained"
            color="aurora"
            onClick={() => setTriggerCopilotOpen(true)}
            disabled={triggerConditionField.field.disabled || nameMissing}
            startIcon={
              <SvgIcon fontSize="large">
                <IconCustomAurora />
              </SvgIcon>
            }
            data-cy="copilot-edit"
          >
            {intl.formatMessage({ id: 'features.rules.copilotEdit' })}
          </Button>
        </Stack>
      </ContentWrapper>
      <TriggerCopilot
        queue={queue}
        open={triggerCopilotOpen}
        onClose={() => setTriggerCopilotOpen(false)}
        onSuccess={response => {
          if (response.triggerCondition) {
            triggerConditionField.field.onChange(response.triggerCondition);

            setTriggerConditionForSummary(response.triggerCondition);
          }

          setTriggerCopilotOpen(false);
        }}
        schemaRule={{ ...currentRuleConcept, schema: schemaUrl }}
      />
    </>
  );
};
